import React, { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useAuth } from '../../context/AuthContext'
import { Button } from '../common/Button'
import { RichTextEditor } from '../common/RichTextEditor'
import { PROD_RELEASE_SCHEDULE } from '../../lib/config/release-config'
import { mutations } from '../../lib/supabase/mutations'
import { queries } from '../../lib/supabase/queries'
import { isSalesLikeRole } from '../../lib/config/role-config'
import { RELATED_APPS, TASK_PRIORITIES, TASK_PRIORITY_LABELS, TASK_TYPES, TASK_TYPE_LABELS } from '../../lib/config/project-config'
import { sendTaskNewApprovalEmail } from '../../utils/emailNotifications'
import { formatReleaseDate, getDaysUntilDate, getNextProdReleaseDate } from '../../utils/releaseSchedule'

type NavIconProps = {
  className?: string
}

const DashboardIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6V11h-6v9Zm0-18v7h6V2h-6Z" />
  </svg>
)

const TasksIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const KanbanIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M4 5h6v14H4zM14 5h6v8h-6zM14 15h6v4h-6z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ManageIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M12 3v4M12 17v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M3 12h4M17 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
)

const RequestIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const { profile, signOut, themePreference, updateThemePreference, user } = useAuth()
  const [isDevRequestOpen, setIsDevRequestOpen] = useState(false)
  const [devRequestForm, setDevRequestForm] = useState<{
    title: string
    requested_by: string
    support_ticket_no: string
    description: string
    type: string
    client_priority: string
    billable: boolean
    related_app_id: string
  }>({
    title: '',
    requested_by: '',
    support_ticket_no: '',
    description: '',
    type: 'bugfix',
    client_priority: 'low',
    billable: false,
    related_app_id: '',
  })
  const [devRequestError, setDevRequestError] = useState<string | null>(null)

  const navItems = useMemo(
    () => [
      { path: '/dashboard', label: 'Dashboard', Icon: DashboardIcon },
      { path: '/tasks', label: 'Tasks', Icon: TasksIcon },
      { path: '/kanban', label: 'Kanban', Icon: KanbanIcon },
      ...(profile?.role === 'admin' ? [{ path: '/manage', label: 'Manage', Icon: ManageIcon }] : []),
    ],
    [profile?.role]
  )

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const relatedAppOptions = relatedAppsQuery.data || RELATED_APPS.map((name) => ({
    id: name,
    name,
    current_version: null,
    next_release_version: null,
    created_at: '',
    updated_at: '',
  }))

  const createDevRequestMutation = useMutation({
    mutationFn: mutations.createTask,
    onSuccess: async (createdTask) => {
      const approverEmails = (profilesQuery.data || [])
        .filter((item) => item.is_task_approver && item.email)
        .map((item) => item.email as string)

      if (createdTask.status === 'new' && approverEmails.length > 0) {
        await sendTaskNewApprovalEmail(approverEmails, createdTask, createdTask.requested_by || 'Unknown requester')
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setIsDevRequestOpen(false)
      setDevRequestForm({
        title: '',
        requested_by: '',
        support_ticket_no: '',
        description: '',
        type: 'bugfix',
        client_priority: 'low',
        billable: false,
        related_app_id: relatedAppOptions[0]?.id || '',
      })
      setDevRequestError(null)
      // Pass the newly created task to TasksPage to open its view modal
      navigate('/tasks', { state: { newlyCreatedTask: createdTask } })
    },
  })

  const handleDevRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!devRequestForm.title.trim()) {
      setDevRequestError('Title is required')
      return
    }

    if (!devRequestForm.requested_by.trim()) {
      setDevRequestError('Requested by (client) is required')
      return
    }

    try {
      const selectedRelatedApp = relatedAppOptions.find((app) => app.id === devRequestForm.related_app_id)

      await createDevRequestMutation.mutateAsync({
        title: devRequestForm.title.trim(),
        requested_by: devRequestForm.requested_by.trim(),
        support_ticket_no: profile?.role === 'support' ? (devRequestForm.support_ticket_no.trim() || null) : null,
        description: devRequestForm.description.trim() || null,
        status: 'new',
        priority: 'low',
        client_priority: devRequestForm.client_priority,
        billable: devRequestForm.billable,
        type: devRequestForm.type,
        related_app_id: selectedRelatedApp?.id || null,
        assigned_to: null,
        created_by: user?.id || '',
        projected_completion: null,
      })
    } catch (error) {
      setDevRequestError(error instanceof Error ? error.message : 'Unable to create dev request')
    }
  }

  const currentPageLabel = useMemo(
    () => navItems.find((item) => location.pathname.startsWith(item.path))?.label || 'Dashboard',
    [location.pathname]
  )

  const nextProdReleaseDate = useMemo(() => getNextProdReleaseDate(PROD_RELEASE_SCHEDULE), [])
  const daysUntilProdRelease = getDaysUntilDate(nextProdReleaseDate)
  const prodReleaseCountdownLabel =
    daysUntilProdRelease === 0 ? 'Today' : `${daysUntilProdRelease} day${daysUntilProdRelease === 1 ? '' : 's'} to go`

  return (
    <div className="min-h-screen bg-stone-300 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-stone-400/70 bg-stone-200/95 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 md:px-8">
          <Link to="/dashboard" className="mr-2 whitespace-nowrap text-lg font-semibold tracking-wide text-slate-900">
            iPulse Systems DoD
          </Link>

          <nav className="flex items-center gap-2">
            {navItems.map(({ path, label, Icon }) => {
              const active = location.pathname.startsWith(path)
              return (
                <Link
                  key={path}
                  to={path}
                  title={label}
                  aria-label={label}
                  className={clsx(
                    'inline-flex h-11 w-11 items-center justify-center rounded-xl border transition',
                    active
                      ? 'border-slate-700 bg-slate-700 text-stone-100 shadow-sm'
                      : 'border-stone-400 bg-stone-100 text-slate-700 hover:border-stone-500 hover:bg-stone-50'
                  )}
                >
                  <Icon className="h-5 w-5" />
                </Link>
              )
            })}

            {isSalesLikeRole(profile?.role) && (
              <button
                type="button"
                title="New Dev Request"
                aria-label="New Dev Request"
                onClick={() => setIsDevRequestOpen(true)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-blue-700 bg-blue-700 text-stone-100 transition hover:bg-blue-800"
              >
                <RequestIcon className="h-5 w-5" />
              </button>
            )}
          </nav>

          <div className="min-w-0 flex-1 text-sm text-slate-600">
            <span className="hidden md:inline">{currentPageLabel}</span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden min-w-[205px] rounded-xl border border-stone-400 bg-stone-100 px-4 py-2 shadow-sm lg:block">
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Next Prod Release</p>
                <p className="text-sm font-semibold text-slate-900">{formatReleaseDate(nextProdReleaseDate)}</p>
              </div>
              <p className="mt-1 text-right text-xs text-slate-600">{prodReleaseCountdownLabel}</p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => updateThemePreference(themePreference === 'light' ? 'dark' : 'light')}
              className="h-11 px-4"
            >
              {themePreference === 'light' ? 'Dark mode' : 'Light mode'}
            </Button>
            <div className="rounded-xl border border-stone-400 bg-stone-100 px-4 py-2 text-right shadow-sm">
              <p className="max-w-[220px] truncate text-sm font-medium text-slate-900">
                {profile?.full_name || profile?.email || 'User'}
              </p>
              <p className="text-xs uppercase tracking-wide text-slate-600">{profile?.role || 'unknown'}</p>
            </div>
            <Button
              onClick={() => signOut()}
              variant="ghost"
              size="sm"
              className="h-11 border border-stone-400 bg-stone-100 px-4 text-slate-800 hover:bg-stone-50"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="min-h-[calc(100vh-76px)] px-5 py-6 md:min-h-[calc(100vh-84px)] md:px-8 md:py-8">{children}</main>

      {isDevRequestOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-stone-400 bg-stone-100 shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-300 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">New Dev Request</h2>
                <p className="text-sm text-slate-600">Submit a development request to the team.</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1 text-slate-500 transition hover:bg-stone-200 hover:text-slate-800"
                onClick={() => {
                  setIsDevRequestOpen(false)
                  setDevRequestError(null)
                }}
              >
                ✕
              </button>
            </div>

            <form className="space-y-6 px-6 py-5" onSubmit={handleDevRequestSubmit}>
              {devRequestError && (
                <div className="rounded-lg border border-red-300 bg-red-100 p-4 text-sm text-red-800">
                  {devRequestError}
                </div>
              )}

              <div className="space-y-4">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">Title *</span>
                  <input
                    className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    type="text"
                    value={devRequestForm.title}
                    onChange={(e) => setDevRequestForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Brief description of the request"
                    required
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">Requested By (client) *</span>
                  <input
                    className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    type="text"
                    value={devRequestForm.requested_by}
                    onChange={(e) => setDevRequestForm((prev) => ({ ...prev, requested_by: e.target.value }))}
                    placeholder="Client name"
                    required
                  />
                </label>

                {profile?.role === 'support' && (
                  <label className="space-y-2">
                    <span className="text-sm font-medium text-slate-700">Support Ticket No</span>
                    <input
                      className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm outline-none focus:border-slate-500"
                      type="text"
                      value={devRequestForm.support_ticket_no}
                      onChange={(e) => setDevRequestForm((prev) => ({ ...prev, support_ticket_no: e.target.value }))}
                      placeholder="e.g. SUP-1024"
                    />
                  </label>
                )}

                <div>
                  <RichTextEditor
                    label="Description"
                    value={devRequestForm.description}
                    onChange={(value) => setDevRequestForm((prev) => ({ ...prev, description: value }))}
                    placeholder="Add context, requirements, or additional details."
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-sm font-medium text-slate-700">Type *</span>
                    <select
                      className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm"
                      value={devRequestForm.type}
                      onChange={(e) => setDevRequestForm((prev) => ({ ...prev, type: e.target.value }))}
                    >
                      {TASK_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {TASK_TYPE_LABELS[type as keyof typeof TASK_TYPE_LABELS]}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-slate-700">Related App *</span>
                    <select
                      className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm"
                      value={devRequestForm.related_app_id}
                      onChange={(e) => setDevRequestForm((prev) => ({ ...prev, related_app_id: e.target.value }))}
                    >
                      {relatedAppOptions.map((app) => (
                        <option key={app.id} value={app.id}>
                          {app.name}
                          {app.current_version ? ` | Current: ${app.current_version}` : ''}
                          {app.next_release_version ? ` | Next: ${app.next_release_version}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-slate-700">Client Priority</span>
                    <select
                      className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm"
                      value={devRequestForm.client_priority}
                      onChange={(e) => setDevRequestForm((prev) => ({ ...prev, client_priority: e.target.value }))}
                    >
                      {TASK_PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {TASK_PRIORITY_LABELS[priority]}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-slate-700">Billable</span>
                    <div className="flex h-[42px] items-center rounded-lg border border-stone-300 bg-stone-50 px-3">
                      <input
                        id="dev-request-billable"
                        type="checkbox"
                        className="h-4 w-4 rounded border-stone-300 text-slate-700 focus:ring-slate-500"
                        checked={devRequestForm.billable}
                        onChange={(e) => setDevRequestForm((prev) => ({ ...prev, billable: e.target.checked }))}
                      />
                      <label htmlFor="dev-request-billable" className="ml-3 text-sm text-slate-700">
                        Mark this request as billable
                      </label>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-stone-300 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDevRequestOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" isLoading={createDevRequestMutation.isPending}>
                  Submit Request
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
